import { useState, useEffect } from 'react';
import { Menu, X, Instagram, Linkedin, Twitter, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import logo from '@/assets/logo.webp';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sections = ['home', 'about', 'services', 'portfolio', 'resume', 'contact'];
    
    const observerOptions = {
      rootMargin: '-100px 0px -66%',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  const navigate = useNavigate();

  const navLinks = [
    { name: 'Home', href: '/#home', isRoute: false },
    { name: 'About', href: '/#about', isRoute: false },
    { name: 'Services', href: '/#services', isRoute: false },
    { name: 'Portfolio', href: '/#portfolio', isRoute: false },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: 'Contact', href: '/#contact', isRoute: false },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/rakibhossentopu/' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/rakibhossentopu/' },
    { icon: Twitter, href: 'https://x.com/rakibhassantopu' },
    { icon: Facebook, href: 'https://www.facebook.com/rakibhossentopu' },
  ];

  const closeMobileMenu = () => {
    setIsMenuClosing(true);
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsMenuClosing(false);
    }, 200);
  };

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string, isRoute: boolean) => {
    if (isRoute) {
      e.preventDefault();
      closeMobileMenu();
      navigate(href);
      return;
    }
    
    e.preventDefault();
    const isHomePage = window.location.pathname === '/';
    const targetId = href.replace('/#', '').replace('#', '');
    
    if (isHomePage) {
      const element = document.getElementById(targetId);
      if (element) {
        const offsetTop = element.offsetTop - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
        closeMobileMenu();
      }
    } else {
      navigate(href);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" title="Rakib Hossen Topu - SEO Expert in BD">
            <img src={logo} alt="Rakib Hossen Topu - SEO Expert in BD | Digital Marketing Specialist Bangladesh" width={70} height={52} className="h-12 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => {
              const isActive = activeSection === link.href.replace('/#', '').replace('/', '');
              const isBlogActive = link.isRoute && window.location.pathname.startsWith('/blog');
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleSmoothScroll(e, link.href, link.isRoute)}
                  className={`transition-colors duration-300 font-medium relative ${
                    isActive || isBlogActive
                      ? 'text-primary' 
                      : 'text-foreground hover:text-primary'
                  }`}
                >
                  {link.name}
                  {(isActive || isBlogActive) && (
                    <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary rounded-full" />
                  )}
                </a>
              );
            })}
          </div>

          {/* Social Links */}
          <div className="hidden md:flex items-center space-x-4">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => isMobileMenuOpen ? closeMobileMenu() : setIsMobileMenuOpen(true)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            {/* Tap-to-close overlay */}
            <div 
              className={`md:hidden fixed inset-0 top-[72px] z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-200 ${
                isMenuClosing ? 'opacity-0' : 'opacity-100'
              }`}
              onClick={closeMobileMenu}
            />
            {/* Menu content */}
            <div className={`md:hidden fixed inset-x-0 top-[72px] bottom-0 z-50 bg-background backdrop-blur-xl shadow-2xl overflow-y-auto transition-all duration-200 ${
              isMenuClosing ? 'opacity-0 translate-y-[-10px]' : 'opacity-100 translate-y-0'
            }`}>
              <div className="flex flex-col p-6 space-y-2">
                {navLinks.map((link) => {
                  const isActive = activeSection === link.href.replace('/#', '').replace('/', '');
                  const isBlogActive = link.isRoute && window.location.pathname.startsWith('/blog');
                  return (
                    <a
                      key={link.name}
                      href={link.href}
                      onClick={(e) => handleSmoothScroll(e, link.href, link.isRoute)}
                      className={`py-4 px-4 rounded-lg transition-all duration-300 font-medium text-lg ${
                        isActive || isBlogActive
                          ? 'text-primary bg-primary/10' 
                          : 'text-foreground hover:text-primary hover:bg-muted'
                      }`}
                    >
                      {link.name}
                    </a>
                  );
                })}
                <div className="flex space-x-6 pt-6 mt-4 border-t border-border justify-center">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors duration-300 p-2"
                    >
                      <social.icon className="w-6 h-6" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
